import React from "react";
import { Link } from "react-router-dom";
import {
    Waves,
    Wifi,
    UtensilsCrossed,
    MapPin,
    ArrowRight,
    Bed,
    Star,
    Instagram,
} from "lucide-react";
import { IMAGES } from "../lib/api";
import { useLanguage } from "../lib/i18n";

const Home = () => {
    const { t } = useLanguage();
    return (
        <div data-testid="home-page" className="bg-background">
            {/* HERO */}
            <section className="relative h-screen w-full overflow-hidden">
                <img
                    src={IMAGES.hero}
                    alt="Villa Alam Lembongan"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Darker overlay — ensures white text is readable over any hero photo */}
                <div className="absolute inset-0 bg-black/40" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/70" />
                <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 fade-in-slow">
                    <div className="overline text-white/90 mb-6" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>
                        {t("hero.tagline")}
                    </div>
                    <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95]" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.5)" }}>
                        Villa Alam Lembongan
                    </h1>
                    <div className="mt-4 text-white/95 font-display italic text-2xl sm:text-3xl" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.6)" }}>
                        {t("hero.subtitle")}
                    </div>
                    <p className="mt-8 max-w-xl text-white text-sm sm:text-base leading-relaxed font-light" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.7)" }}>
                        {t("hero.description")}
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/gallery"
                            data-testid="hero-gallery-btn"
                            className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-secondary text-white text-xs tracking-[0.2em] uppercase hover:bg-secondary/90 transition-colors"
                        >
                            {t("hero.viewVilla")} <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="hero-contact-btn"
                            className="inline-flex items-center justify-center px-10 py-4 border border-white/80 text-white text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-primary transition-colors"
                        >
                            {t("hero.getInTouch")}
                        </Link>
                    </div>
                </div>
                <div className="absolute bottom-8 inset-x-0 flex justify-center text-white/60 overline text-[10px]">
                    {t("cta.scroll")}
                </div>
            </section>

            {/* STORY */}
            <section
                data-testid="home-story"
                className="py-24 lg:py-40 bg-background relative"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-5">
                        <div className="overline text-secondary mb-6">
                            {t("home.story.overline")}
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-primary tracking-tight">
                            {t("home.story.title.a")}
                            <span className="italic"> {t("home.story.title.b")} </span>
                            {t("home.story.title.c")}
                        </h2>
                        <div className="mt-8 space-y-5 text-muted-foreground leading-relaxed font-light">
                            <p>{t("home.story.p1")}</p>
                            <p>{t("home.story.p2")}</p>
                            <p>{t("home.story.p3")}</p>
                        </div>
                    </div>
                    <div className="lg:col-span-7 relative">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img
                                src={IMAGES.story}
                                alt="Heritage village house"
                                className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105"
                            />
                        </div>
                        <div className="absolute -bottom-8 -left-8 hidden md:block bg-background border border-border p-8 max-w-xs shadow-sm">
                            <div className="overline text-secondary">
                                {t("home.story.badge")}
                            </div>
                            <div className="font-display text-2xl mt-2 text-primary leading-tight">
                                {t("home.story.badgeText")}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HIGHLIGHTS */}
            <section
                data-testid="home-highlights"
                className="bg-muted py-24 lg:py-32 relative grain-overlay"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid md:grid-cols-4 gap-10 text-center md:text-left">
                        {[
                            { icon: Bed, label: t("label.bedrooms") },
                            { icon: Waves, label: t("home.highlights.privatePool") },
                            { icon: MapPin, label: t("home.highlights.beach") },
                            { icon: UtensilsCrossed, label: t("home.highlights.restaurants") },
                        ].map((h, i) => (
                            <div
                                key={i}
                                className="flex flex-col md:flex-row items-center md:items-start gap-4"
                            >
                                <h.icon
                                    size={28}
                                    strokeWidth={1.25}
                                    className="text-secondary shrink-0"
                                />
                                <div>
                                    <div className="font-display text-xl text-primary">
                                        {h.label}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* AMENITIES TEASER */}
            <section
                data-testid="home-amenities"
                className="py-24 lg:py-32 bg-background"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="max-w-2xl mb-16">
                        <div className="overline text-secondary mb-4">
                            {t("home.amenities.overline")}
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-light text-primary leading-tight tracking-tight">
                            {t("home.amenities.title")}
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: Waves,
                                title: t("home.amenities.pool.title"),
                                text: t("home.amenities.pool.text"),
                            },
                            {
                                icon: UtensilsCrossed,
                                title: t("home.amenities.kitchen.title"),
                                text: t("home.amenities.kitchen.text"),
                            },
                            {
                                icon: Wifi,
                                title: t("home.amenities.wifi.title"),
                                text: t("home.amenities.wifi.text"),
                            },
                        ].map((a, i) => (
                            <div
                                key={i}
                                className="p-10 border border-border bg-card hover:border-primary/30 transition-all duration-500 hover:-translate-y-1"
                            >
                                <a.icon
                                    size={32}
                                    strokeWidth={1.25}
                                    className="text-secondary"
                                />
                                <h3 className="font-display text-2xl mt-6 text-primary">
                                    {a.title}
                                </h3>
                                <p className="mt-3 text-sm text-muted-foreground leading-relaxed font-light">
                                    {a.text}
                                </p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12 flex justify-center">
                        <Link
                            to="/amenities"
                            data-testid="home-amenities-link"
                            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary border-b border-primary pb-1 hover:text-secondary hover:border-secondary transition-colors"
                        >
                            {t("cta.allAmenities")} <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* SIGNATURE DETAIL — Soak Under the Stars */}
            <section
                data-testid="home-bathtub"
                className="bg-primary text-white relative overflow-hidden"
            >
                <div className="grid lg:grid-cols-12 min-h-[90vh]">
                    <div className="lg:col-span-7 relative aspect-[4/5] lg:aspect-auto">
                        <img
                            src="https://customer-assets.emergentagent.com/job_bali-escape/artifacts/s33wholr_Image%20%2888%29.jpeg"
                            alt="Freestanding bathtub under skylight"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-primary/30" />
                    </div>
                    <div className="lg:col-span-5 flex items-center px-8 sm:px-12 lg:px-16 py-20 lg:py-0">
                        <div>
                            <div className="overline text-secondary mb-6">
                                {t("home.signature.overline")}
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light italic leading-[1.05] tracking-tight">
                                {t("home.signature.title")}
                            </h2>
                            <div className="mt-8 space-y-5 text-white/80 leading-relaxed font-light">
                                <p>{t("home.signature.p1")}</p>
                                <p>{t("home.signature.p2")}</p>
                            </div>
                            <Link
                                to="/rooms"
                                data-testid="home-bathtub-cta"
                                className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white border-b border-white pb-1 hover:text-secondary hover:border-secondary transition-colors"
                            >
                                See the Master Bedroom <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* GALLERY PREVIEW */}
            <section
                data-testid="home-gallery-preview"
                className="py-24 lg:py-32 bg-muted"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                        <div>
                            <div className="overline text-secondary mb-4">
                                {t("home.gallery.overline")}
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl font-light text-primary tracking-tight">
                                {t("home.gallery.title")}
                            </h2>
                        </div>
                        <Link
                            to="/gallery"
                            data-testid="home-view-gallery"
                            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary hover:text-secondary transition-colors"
                        >
                            {t("cta.fullGallery")} <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {IMAGES.gallery.slice(0, 8).map((url, i) => (
                            <div
                                key={i}
                                className={`overflow-hidden ${
                                    i === 0 || i === 5
                                        ? "md:row-span-2 md:aspect-[3/4]"
                                        : "aspect-square"
                                }`}
                            >
                                <img
                                    src={url}
                                    alt={`Villa scene ${i + 1}`}
                                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LOCATION TEASER */}
            <section
                data-testid="home-location"
                className="py-24 lg:py-32 bg-background"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="overline text-secondary mb-4">
                            {t("home.location.overline")}
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-primary leading-tight tracking-tight">
                            {t("home.location.title")}
                        </h2>
                        <p className="mt-6 text-muted-foreground leading-relaxed font-light max-w-lg">
                            {t("home.location.intro")}
                        </p>
                        <div className="mt-10 grid grid-cols-2 gap-6">
                            {[
                                { place: "Jungutbatu Beach", dist: "4 min walk" },
                                { place: "Panorama Point", dist: "1.7 km" },
                                { place: "Devil's Tear", dist: "10 min scooter" },
                                { place: "Airport (DPS)", dist: "81 km + boat" },
                            ].map((p) => (
                                <div
                                    key={p.place}
                                    className="border-t border-border pt-4"
                                >
                                    <div className="font-display text-xl text-primary">
                                        {p.place}
                                    </div>
                                    <div className="text-xs tracking-widest uppercase text-muted-foreground mt-1">
                                        {p.dist}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Link
                            to="/location"
                            data-testid="home-location-link"
                            className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary border-b border-primary pb-1 hover:text-secondary hover:border-secondary transition-colors"
                        >
                            {t("cta.thingsToDo")} <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="aspect-[4/5] overflow-hidden">
                        <img
                            src={IMAGES.location[0]}
                            alt="Lembongan beach"
                            className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105"
                        />
                    </div>
                </div>
            </section>

            {/* TESTIMONIAL */}
            <section
                data-testid="home-testimonial"
                className="py-24 lg:py-32 bg-primary text-white relative overflow-hidden"
            >
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <div className="overline text-secondary mb-8">
                        {t("home.testimonial.overline")}
                    </div>
                    <blockquote className="font-display text-3xl sm:text-4xl lg:text-5xl font-light italic leading-[1.2] text-white">
                        "{t("home.testimonial.quote")}"
                    </blockquote>
                    <div className="mt-10 overline text-white/70">
                        {t("home.testimonial.author")}
                    </div>

                    {/* Google Reviews Badge */}
                    <a
                        href="https://www.google.com/maps/search/?api=1&query=Villa+Alam+Lembongan"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="google-reviews-badge"
                        className="mt-12 inline-flex items-center gap-4 px-6 py-4 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-colors"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <div className="flex items-center gap-1 text-secondary">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    size={14}
                                    fill="currentColor"
                                    strokeWidth={0}
                                />
                            ))}
                        </div>
                        <span className="text-sm tracking-[0.15em] uppercase text-white">
                            Loved by guests on Google
                        </span>
                    </a>
                </div>
            </section>

            {/* INSTAGRAM FEED */}
            <section
                data-testid="home-instagram"
                className="py-24 lg:py-32 bg-background"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                        <div>
                            <div className="overline text-secondary mb-4 flex items-center gap-2">
                                <Instagram size={14} strokeWidth={1.5} />
                                {t("home.instagram.overline")}
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-primary tracking-tight leading-[1.05]">
                                @villa_alam_lembongan
                            </h2>
                            <p className="mt-4 text-muted-foreground font-light max-w-md">
                                {t("home.instagram.intro")}
                            </p>
                        </div>
                        <a
                            href="https://instagram.com/villa_alam_lembongan"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="home-instagram-follow"
                            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary border-b border-primary pb-1 hover:text-secondary hover:border-secondary transition-colors"
                        >
                            {t("cta.followInstagram")} <ArrowRight size={14} />
                        </a>
                    </div>

                    {/* Story strip — circular reels */}
                    <div className="flex gap-5 overflow-x-auto pb-2 mb-10 scrollbar-hide">
                        {[
                            { label: "Tour", img: IMAGES.gallery[0] },
                            { label: "Pool", img: IMAGES.gallery[5] },
                            { label: "Bath", img: IMAGES.gallery[1] },
                            { label: "Master", img: IMAGES.gallery[2] },
                            { label: "Beach", img: IMAGES.gallery[10] },
                            { label: "Sunset", img: IMAGES.gallery[8] },
                            { label: "Eats", img: IMAGES.gallery[14] },
                        ].map((s, i) => (
                            <a
                                key={i}
                                href="https://instagram.com/villa_alam_lembongan"
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`ig-story-${i}`}
                                className="flex flex-col items-center gap-2 shrink-0 group"
                            >
                                <div className="w-20 h-20 rounded-full p-[2.5px] bg-gradient-to-tr from-secondary via-rose-400 to-secondary">
                                    <div className="w-full h-full rounded-full p-[2px] bg-background">
                                        <img
                                            src={s.img}
                                            alt={s.label}
                                            className="w-full h-full rounded-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                </div>
                                <div className="text-xs text-muted-foreground tracking-wider">
                                    {s.label}
                                </div>
                            </a>
                        ))}
                    </div>

                    {/* Insta-style grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                        {IMAGES.gallery.slice(2, 11).map((url, i) => (
                            <a
                                key={i}
                                href="https://instagram.com/villa_alam_lembongan"
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`ig-post-${i}`}
                                className="relative aspect-square overflow-hidden group"
                            >
                                <img
                                    src={url}
                                    alt={`@villa_alam_lembongan post ${i + 1}`}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/40 transition-colors flex items-center justify-center">
                                    <Instagram
                                        size={32}
                                        strokeWidth={1.25}
                                        className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    />
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
